import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  User,
  Zap,
  Shield,
  Clock,
  Sun,
  Moon,
  ChevronRight,
  Star,
  Mail,
  Languages,
  Loader2
} from 'lucide-react';
import { HashRouter as Router, Routes, Route, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { generateResponse } from '@/lib/deepseek';
import ContactSales from './pages/ContactSales';
import ContactForm from '@/components/ContactForm';
import Product from './pages/Product';
import Solutions from './pages/Solutions';
import Enterprise from './pages/Enterprise';
import StartPOC from './pages/StartPOC';
import { animations } from '@/lib/animations';
import { CALENDLY_URL, ROUTES } from '@/lib/constants';
import PricingSection from '@/components/PricingSection';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';
import { chatAnalytics } from '@/lib/analytics';
import ChatDashboard from '@/components/ChatDashboard';
import { AuthProvider } from '@/contexts/AuthContext';
import AdminLogin from '@/components/AdminLogin';
import ProtectedRoute from '@/components/ProtectedRoute';
import PricingPage from './pages/Pricing';
import ResourcesPage from './pages/Resources';
import { Analytics } from '@vercel/analytics/react';
import { Toaster } from "@/components/ui/toaster";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AnimatedCard, AnimatedButton, AnimatedText } from '@/components/ui/animated-card';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface PricingPlan {
  title: string;
  price: string;
  period?: string;
  features: string[];
}

// Add ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const Layout = () => {
  const navigate = useNavigate();
  const { language, setLanguage, theme, setTheme } = useLanguage();
  const t = translations[language];
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}
    >
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 w-full bg-gradient-to-r from-primary/5 via-background to-primary/5 dark:from-primary/10 dark:via-background dark:to-primary/10 z-50 border-b border-primary/10 dark:border-primary/20 backdrop-blur-md ${animations.glassEffect}`}
      >
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <motion.div 
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-8"
          >
            <Link to="/" className={`flex items-center gap-2 ${animations.linkHover}`}>
              <motion.img 
                src="/logo.png" 
                alt="Logo" 
                className="w-6 h-6"
                whileHover={{ 
                  rotate: 5,
                  scale: 1.1,
                  transition: { duration: 0.3 }
                }}
              />
              <motion.span 
                className="font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                {t.nav.title}
              </motion.span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {[
                { path: ROUTES.PRODUCT, label: t.nav.product },
                { path: ROUTES.SOLUTIONS, label: t.nav.solutions },
                { path: ROUTES.ENTERPRISE, label: t.nav.enterprise },
              ].map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <Link 
                    to={item.path}
                    className={`text-sm font-medium text-muted-foreground hover:text-primary transition-colors duration-200 relative group ${animations.linkHover}`}
                  >
                    {item.label}
                    <motion.span 
                      className="absolute bottom-0 left-0 w-full h-0.5 bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                    />
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
          <motion.div 
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center gap-2"
          >
            <Button 
              variant="ghost" 
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-2 hover:bg-primary/10 group"
            >
              <Languages className="h-4 w-4 group-hover:rotate-12 transition-transform duration-300" />
              <span>{language === 'en' ? 'English' : 'Español'}</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(ROUTES.CONTACT_SALES)}
              className="hover:bg-primary/10 group"
            >
              {t.nav.talkToSales}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="ml-2 h-8 w-8 hover:bg-primary/10 group"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
              ) : (
                <Moon className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
              )}
            </Button>
          </motion.div>
        </div>
      </motion.nav>
      <motion.main 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="pt-16"
      >
        <AnimatePresence mode="wait">
          <Outlet />
        </AnimatePresence>
      </motion.main>
    </motion.div>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <ScrollToTop />
          <Toaster />
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="product" element={<Product />} />
              <Route path="solutions" element={<Solutions />} />
              <Route path="enterprise" element={<Enterprise />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="resources" element={<ResourcesPage />} />
              <Route path="contact-sales" element={<ContactSales />} />
              <Route path="start-poc" element={<StartPOC />} />
              <Route path="admin" element={<AdminLogin />} />
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute>
                    <ChatDashboard />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
          <Analytics />
          <SpeedInsights />
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

// Separar el componente Home
const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: t.home.demo.initial }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Sugerencias de preguntas comunes
  const suggestedQuestions = [
    '¿Cómo funciona la prueba de concepto?',
    '¿Qué industrias pueden beneficiarse?',
    '¿Cuánto tiempo toma la implementación?',
    '¿Qué canales de comunicación soportan?'
  ];

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const startTime = Date.now();
    const userMessage: Message = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await generateResponse(inputMessage);
      const assistantMessage: Message = { role: 'assistant', content: response };
      setMessages(prev => [...prev, assistantMessage]);

      // Registrar la interacción exitosa
      chatAnalytics.logInteraction({
        timestamp: startTime,
        userMessage: inputMessage,
        botResponse: response,
        responseTime: Date.now() - startTime,
        isError: false
      });
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage = 'Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);

      // Registrar la interacción fallida
      chatAnalytics.logInteraction({
        timestamp: startTime,
        userMessage: inputMessage,
        botResponse: errorMessage,
        responseTime: Date.now() - startTime,
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInputMessage(question);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <section className="flex flex-col items-center justify-center min-h-[90vh] pb-8 pt-6 md:pt-10 md:pb-12 lg:pt-16 lg:pb-24 relative overflow-hidden w-full">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative mx-auto flex max-w-[1200px] flex-col items-center gap-8 text-center px-4"
        >
          <motion.div 
            className={`inline-block p-3 rounded-2xl bg-primary/10 mb-6 shadow-lg shadow-primary/20`}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 260,
              damping: 20,
              duration: 1
            }}
            whileHover={{ 
              scale: 1.05,
              rotate: 5,
              transition: { duration: 0.3 }
            }}
          >
            <img src="/logo.png" alt="Logo" className="w-12 h-12" />
          </motion.div>
          
          <motion.h1 
            className={`text-4xl font-bold leading-tight tracking-tighter md:text-6xl lg:leading-[1.1] bg-gradient-to-r from-primary via-primary/80 to-primary/50 bg-clip-text text-transparent`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20,
              delay: 0.3
            }}
          >
            {t.home.hero.title}
          </motion.h1>
          
          <motion.p 
            className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20,
              delay: 0.5
            }}
          >
            {t.home.hero.subtitle}
          </motion.p>
          
          <motion.div 
            className={`flex flex-col gap-4 sm:flex-row sm:gap-6`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 20,
              delay: 0.7
            }}
          >
            <Button 
              size="lg" 
              className="group text-lg px-8 py-6 rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 relative overflow-hidden"
              onClick={() => navigate(ROUTES.START_POC)}
            >
              <motion.span
                className="relative z-10"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                {t.home.hero.startPOC}
                <ArrowRight className="ml-2 h-5 w-5 inline-block group-hover:translate-x-1 transition-transform" />
              </motion.span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 1, repeat: Infinity, repeatType: "reverse" }}
              />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-lg px-8 py-6 rounded-xl border-primary/20 hover:border-primary/40 transition-all duration-300 group"
              onClick={() => navigate(ROUTES.CONTACT_SALES)}
            >
              <motion.span
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                {t.home.hero.scheduleDemo}
              </motion.span>
            </Button>
          </motion.div>
        </motion.div>
      </section>

      <section className={`container py-16 md:py-24 lg:py-32 bg-muted/30 ${animations.fadeIn}`}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto flex max-w-[1100px] flex-col items-center gap-12"
        >
          <h2 className={`text-2xl font-bold leading-tight tracking-tighter md:text-4xl text-center font-display ${animations.fadeInSlideUp}`}>
            {t.home.demo.title}
          </h2>
          <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center font-sans ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.home.demo.subtitle}
          </p>
          <div className={`w-full max-w-[700px] ${animations.fadeInSlideUp} ${animations.delay200}`}>
            <Card className="relative shadow-xl shadow-primary/10 border-primary/10 rounded-2xl overflow-hidden backdrop-blur-sm bg-background/80">
              <div
                ref={chatContainerRef}
                className="h-[400px] overflow-y-auto p-6 space-y-6"
              >
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex items-start gap-3 max-w-[80%] ${
                        message.role === 'user'
                          ? 'ml-auto bg-primary text-primary-foreground'
                          : 'bg-muted'
                      } rounded-2xl p-4 ${
                        message.role === 'user' ? 'rounded-br-none' : 'rounded-bl-none'
                      } shadow-sm`}
                    >
                      {message.role === 'assistant' && (
                        <img src="/logo.png" alt="Logo" className="w-6 h-6 mt-0.5" />
                      )}
                      <div className="space-y-1">
                        <p className="text-base leading-relaxed">{message.content}</p>
                      </div>
                      {message.role === 'user' && (
                        <img src="/logo.png" alt="Logo" className="w-6 h-6 mt-0.5" />
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-2xl p-4 rounded-bl-none flex items-center gap-2 shadow-sm">
                      <img src="/logo.png" alt="Logo" className="w-6 h-6" />
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>

              {/* Sugerencias */}
              <div className="p-4 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((question, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSuggestedQuestion(question)}
                      className="text-sm rounded-full border-primary/20 hover:border-primary/40 transition-all duration-300"
                    >
                      {question}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Input form */}
              <form onSubmit={handleSubmit} className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder={t.home.demo.placeholder}
                    className="flex-1 rounded-xl border-primary/20 focus:border-primary/40 transition-all duration-300"
                    disabled={isLoading}
                  />
                  <Button 
                    type="submit" 
                    disabled={isLoading || !inputMessage.trim()}
                    className="rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t.home.demo.send
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </motion.div>
      </section>

      <section className={`container py-16 md:py-24 lg:py-32 ${animations.fadeIn}`}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto flex max-w-[1100px] flex-col items-center gap-12"
        >
          <h2 className={`text-2xl font-bold leading-tight tracking-tighter md:text-4xl text-center font-display ${animations.fadeInSlideUp}`}>
            {t.home.features.title}
          </h2>
          <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center font-sans ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.home.features.subtitle}
          </p>
          <div className={`grid gap-8 sm:grid-cols-2 lg:grid-cols-4 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.home.features.items.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className={`p-6 border-primary/10 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 rounded-2xl group hover:scale-105 ${animations.fadeInSlideUp}`}
                >
                  <div className="flex flex-col gap-4">
                    <div className="p-2 bg-primary/10 rounded-xl w-fit shadow-sm group-hover:bg-primary/20 transition-colors duration-300">
                      <Shield className="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h3 className="text-xl font-bold font-display">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed font-sans">{feature.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      <PricingSection />

      <section className={`container py-16 md:py-24 lg:py-32 bg-muted/30 ${animations.fadeIn}`}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto flex max-w-[980px] flex-col items-center gap-12"
        >
          <h2 className={`text-3xl font-bold leading-tight tracking-tighter md:text-5xl text-center font-display ${animations.fadeInSlideUp}`}>
            {t.faq.title}
          </h2>
          <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center font-sans ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.faq.subtitle}
          </p>
          <div className={`w-full max-w-[700px] ${animations.fadeInSlideUp} ${animations.delay200}`}>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {t.faq.questions.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border-primary/10 rounded-xl overflow-hidden hover:bg-muted/50 transition-colors duration-300"
                >
                  <AccordionTrigger className="text-lg font-medium font-display hover:no-underline px-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed font-sans px-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </motion.div>
      </section>

      <section className={`container py-16 md:py-24 lg:py-32 ${animations.fadeIn}`}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto flex max-w-[980px] flex-col items-center gap-12"
        >
          <h2 className={`text-3xl font-bold leading-tight tracking-tighter md:text-5xl text-center font-display ${animations.fadeInSlideUp}`}>
            {t.contact.title}
          </h2>
          <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center font-sans ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.contact.subtitle}
          </p>
          <div className={`w-full max-w-[850px] ${animations.fadeInSlideUp} ${animations.delay200}`}>
            <Card className="p-8 shadow-xl shadow-primary/10 border-primary/10 rounded-2xl backdrop-blur-sm bg-background/80">
              <ContactForm translations={t.contact} />
            </Card>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-primary/10 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="Logo" className="w-6 h-6" />
                <span className="font-display font-bold">Automata AI</span>
              </Link>
              <p className="text-sm text-muted-foreground font-sans max-w-md">
                Transformando el futuro con inteligencia artificial.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://www.linkedin.com/company/automata-artificial-intelligence/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-muted hover:bg-primary/10 transition-colors duration-300 group"
                >
                  <svg 
                    className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
                <a 
                  href="https://x.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-muted hover:bg-primary/10 transition-colors duration-300 group"
                >
                  <svg 
                    className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a 
                  href="https://github.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-muted hover:bg-primary/10 transition-colors duration-300 group"
                >
                  <svg 
                    className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-display font-semibold">Producto</h4>
              <ul className="space-y-2">
                <li><Link to="/product" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 font-sans">Características</Link></li>
                <li><Link to="/solutions" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 font-sans">Soluciones</Link></li>
                <li><Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 font-sans">Precios</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-primary/10 text-center">
            <p className="text-sm text-muted-foreground font-sans">
              © {new Date().getFullYear()} Automata AI. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;